import React from "react";
import { object, shape } from "prop-types";
import { LazyLoadImages } from "@quintype/components";
import { useSelector } from "react-redux";
import get from "lodash/get";
import { ResearchHubRow } from "../collection-templates/research-hub-row";
import LoadingIndicatorComponent from "../atoms/loading-indicator";
import { RESEARCH_HUB_CONTENT } from "../../constants";
import "./research-hub.m.css";

/**
 * Helper function to check if collection has valid items
 * @param {Object} collection - The collection object
 * @returns {boolean} - True if collection has items, false otherwise
 */
const hasCollectionItems = (collection) => {
  const items = get(collection, ['items'], []);
  return items.length > 0;
};

/**
 * Helper function to check if any child collection has items
 * @param {Object} collection - The main collection object
 * @returns {boolean} - True if any child collection has items, false otherwise
 */
const hasAnyChildCollectionItems = (collection) => {
  const childCollections = get(collection, ['items'], []);
  return childCollections.some(childCollection => {
    const childItems = get(childCollection, ['items'], []);
    return childItems.length > 0;
  });
};

/**
 * Helper function to render the no content message
 * @param {Object} collection - The collection object
 * @returns {JSX.Element} - No content message component
 */
const renderNoContentMessage = (collection) => (
  <div className="container" styleName="research-hub-page">
    <h1 styleName="research-hub-page-title">{collection?.name}</h1>
    <p styleName="research-hub-no-content">{RESEARCH_HUB_CONTENT.NO_CONTENT_MESSAGE}</p>
  </div>
);


/**
 * ResearchHub Page Component
 *
 * This page serves as a central hub displaying collections of research stories.
 * Each collection is rendered as a section with story cards in a responsive grid layout.
 *
 * Features:
 * - Multiple collection sections with individual titles
 * - Responsive 3-column grid (desktop), 2-column (tablet), 1-column (mobile)
 * - Optional custom background colors for specific rows
 * - Story cards with category tags, headlines, descriptions, dates, and tags
 * - Entity attribute icons on story images
 * - Date format: "mmm yyyy" (e.g., "Sep 2025")
 */
export const ResearchHubPage = (props) => {
  const member = useSelector((state) => get(state, ["member"], null));
  const subscriberMetadata = useSelector((state) => get(state, ["subscriberMetadata"], null));
  const isAuthInitializing = useSelector((state) => get(state, ["isAuthInitializing"], false));
  const collection = get(props, ['data', 'collection']);

  // Show loading while auth is initializing or waiting for subscriber metadata for logged-in users
  if (isAuthInitializing || (member && !subscriberMetadata)) {
    return <LoadingIndicatorComponent />;
  }

  if (!hasCollectionItems(collection) || !hasAnyChildCollectionItems(collection)) {
    return renderNoContentMessage(collection);
  }

  // Render rows and track if any actually render content
  const renderedRows = collection?.items?.map((item, index) => (
    <ResearchHubRow key={item.id || index} collection={item} index={index} />
  )).filter(Boolean); // Remove null rows

  // If all rows are null after filtering, show no content message
  if (!renderedRows || renderedRows.length === 0) {
    return renderNoContentMessage(collection);
  }

  return (
    <div styleName="research-hub-page">
      <h1 styleName="research-hub-page-title">{collection?.name}</h1>
      <LazyLoadImages>
        {renderedRows}
      </LazyLoadImages>
    </div>
  );
};

ResearchHubPage.propTypes = {
  data: shape({
    collection: object,
  }),
};
